import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Chip, Grid, Stack, TableCell, TableRow, Typography, useTheme } from "@mui/material"
import { MultilangJob } from "src/services/api/document"
import { ViewLinkButton, languagesRowLabelColor } from "./ViewLinkButton"
import { CsvDialog } from "./CsvDialog"

const formatter = new Intl.NumberFormat("en-US")

const WordsBadge = ({ label }: { label: string }) => (<Chip label={label} size="small" sx={{
  fontSize: "0.5rem",
  lineHeight: "0.7rem",
  fontWeight: 600,
  backgroundColor: "#F2F2F2",
  height: "auto",
  "& .MuiChip-label": {
    py: 0.2,
    px: 0.65
  }
}} />)

export { WordsBadge }

const buildWordCountsCsv = (multilangJob: MultilangJob) => {
  const headers = ["Target Language", "New Assets", "Repeated Assets", "Recycled Assets", "Total"]
  const rows = multilangJob.singleLangJobs.map((job) => {
    const newAssets = job.newAssetsWordCount ?? 0
    const repeatedAssets = job.repeatedAssetsWordCount ?? 0
    const recycledAssets = job.recycledAssetsWordCount ?? 0
    const total = newAssets + repeatedAssets + recycledAssets
    return [job.targetLanguage, newAssets, repeatedAssets, recycledAssets, total]
  })
  return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n")
}

export const WordCountsRow = ({ multilangJob }: { multilangJob: MultilangJob }) => {
  const { t } = useTranslation("multilang")
  const { palette } = useTheme()
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)
  const hasWordCounts =
    multilangJob.newAssetsWordCount != undefined &&
    multilangJob.repeatedAssetsWordCount != undefined &&
    multilangJob.recycledAssetsWordCount != undefined

  const totalWordCount =
    (multilangJob.newAssetsWordCount ?? 0) +
    (multilangJob.repeatedAssetsWordCount ?? 0) +
    (multilangJob.recycledAssetsWordCount ?? 0)

  if (!hasWordCounts) {
    return <></>
  }

  const valueStyle = {
    backgroundColor: "white",
    borderRadius: "50px",
    px: 2,
    textAlign: "right",
    lineHeight: "28px"
  }

  const csvContent = buildWordCountsCsv(multilangJob)

  const rows: Array<{ labelKey: string; value: number; labelStyle?: object; valueStyleOverride?: object; gridStyle?: object }> = [
    { labelKey: "newAssets", value: multilangJob.newAssetsWordCount ?? 0 },
    { labelKey: "repeatedAssets", value: multilangJob.repeatedAssetsWordCount ?? 0 },
    { labelKey: "recycledAssets", value: multilangJob.recycledAssetsWordCount ?? 0 },
    {
      labelKey: "total",
      value: totalWordCount,
      labelStyle: { fontWeight: "bold" },
      valueStyleOverride: { backgroundColor: languagesRowLabelColor(multilangJob, palette) },
      gridStyle: { mt: 0.5 }
    }
  ]

  return (
    <>
      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
        <TableCell sx={{ verticalAlign: "top" }}>
          <Stack spacing={1}>
            <Typography>{t("wordCounts")}</Typography>
            <ViewLinkButton onClick={() => setCsvDialogOpen(true)} multilangJob={multilangJob}>View CSV file</ViewLinkButton>
          </Stack>
        </TableCell>
        <TableCell>
          <Grid container spacing={0.5}>
            {rows.map((row) => (
              <React.Fragment key={row.labelKey}>
                <Grid item xs={4} sx={row.gridStyle}>
                  <Typography variant="h6" sx={row.labelStyle}>
                    {t(row.labelKey)}
                  </Typography>
                </Grid>
                <Grid item xs={8} sx={{ textAlign: "right" }}>
                  <Typography variant="h6" sx={{ ...valueStyle, ...row.valueStyleOverride }}>
                    {formatter.format(row.value)}
                    <Box component="span" sx={{ ml: "5px" }}>
                      <WordsBadge label={t("words")} />
                    </Box>
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </TableCell>
      </TableRow>
      <CsvDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        title={t("wordCounts")}
        csvContent={csvContent}
        downloadFilename="word-counts.csv"
      />
    </>
  )
}
